import { Add, Check, Expand } from "components/common/Icons";
import { useState, useEffect, useRef } from "react";
import { TYPO } from "styles/Typo";
import { calculrateReturnByLogReturn, sliceData } from "./PerformanceCharts";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { trackPageAction } from "lib/analytics/core/actions";

function StrategyCalculator({
  logMock,
  realtimeData,
}: {
  logMock: any;
  realtimeData: any;
}) {
  const router = useRouter();
  const { t } = useTranslation("strategy");
  const realTimeLogMean = realtimeData?.assets?.length
    ? realtimeData.assets.reduce(
        (acc: number, curr: any) => acc + curr?.PRICE_CHG_LOG,
        0
      ) / realtimeData?.assets?.length
    : null;
  const assetLog = realtimeData?.assets?.length
    ? [
        ...logMock,
        {
          DAILY_CHG_LOG: realTimeLogMean,
          BASE_DT: new Date().toISOString().split("T")[0],
        },
      ]?.filter((log: any) => log?.DAILY_CHG_LOG !== null)
    : logMock;
  const returnList: any = {
    "3개월": calculrateReturnByLogReturn([
      ...sliceData(assetLog, "3개월").map((log: any) => log?.DAILY_CHG_LOG),
    ]),
    "6개월": calculrateReturnByLogReturn([
      ...sliceData(assetLog, "6개월").map((log: any) => log?.DAILY_CHG_LOG),
    ]),
    "1년": calculrateReturnByLogReturn([
      ...sliceData(assetLog, "1년").map((log: any) => log?.DAILY_CHG_LOG),
    ]),
  };
  const [currentPeriod, setCurrentPeriod] = useState("3개월");
  const [periodSelectModal, setPeriodSelectModal] = useState(false);
  const [viewExpectedProfit, setViewExpectedProfit] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const selectBoxRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        selectBoxRef.current &&
        !selectBoxRef.current.contains(event.target)
      ) {
        setPeriodSelectModal(false); // 외부 클릭시 툴팁 숨기기
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 매수 금액
  const [buyAmount, setBuyAmount] = useState(
    router.locale === "ko" ? 5000000 : 5000
  );
  // 총 예상 수익률
  const [totalProfit, setTotalProfit] = useState(returnList[currentPeriod]);

  const 단위 = router.locale === "ko" ? 1000000 : 1000;
  const max = router.locale === "ko" ? 100000000000 : 100000000;
  const min = router.locale === "ko" ? 1000000 : 1000;

  const periodConvertEng = (period: string) => {
    switch (period) {
      case "3개월":
        return "3-month";
      case "6개월":
        return "6-month";
      case "1년":
        return "1 year";
      case "전체":
        return "all";
      default:
        return "all";
    }
  };

  return (
    <div className="flex flex-col no-drag">
      <div className="pt-5 pl-5 flex flex-col gap-1">
        <div className="relative w-fit">
          <div
            className="flex items-center cursor-pointer gap-1 w-fit p-1 hover:bg-gray2 rounded-[8px]"
            onClick={() => {
              setPeriodSelectModal(true);
            }}
            ref={selectBoxRef}
          >
            <span className={`${TYPO.TYPO5_1_SB} text-gray80`}>
              {router.locale === "ko"
                ? `${currentPeriod}전에 전략대로 투자했다면`
                : `If purchased ${periodConvertEng(currentPeriod)} ago`}
            </span>
            <Expand fill="gray50" height={16} width={16} />
          </div>
          {periodSelectModal && (
            <div
              className="absolute top-[36px] left-[0px] p-3 rounded-[12px] bg-white flex flex-col gap-2 w-[160px]"
              ref={tooltipRef}
              style={{
                boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.16)",
                WebkitBoxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.16)",
              }}
            >
              <div className="flex flex-col gap-2">
                <div
                  className="flex items-center justify-between cursor-pointer p-2"
                  onClick={() => {
                    setCurrentPeriod("3개월");
                    setTotalProfit(returnList["3개월"]);
                    setPeriodSelectModal(false);
                  }}
                >
                  <span className={`${TYPO.TYPO5_1_MD} text-gray80 truncate`}>
                    {router.locale === "ko"
                      ? "3개월"
                      : periodConvertEng("3개월")}
                  </span>
                  <div className={currentPeriod === "3개월" ? "" : "hidden"}>
                    <Check fill={"blue40"} height={20} width={20} />
                  </div>
                </div>
                <div
                  className="flex items-center justify-between cursor-pointer p-2"
                  onClick={() => {
                    setCurrentPeriod("6개월");
                    setTotalProfit(returnList["6개월"]);
                    setPeriodSelectModal(false);
                  }}
                >
                  <span className={`${TYPO.TYPO5_1_MD} text-gray80 truncate`}>
                    {router.locale === "ko"
                      ? "6개월"
                      : periodConvertEng("6개월")}
                  </span>
                  <div className={currentPeriod === "6개월" ? "" : "hidden"}>
                    <Check fill={"blue40"} height={20} width={20} />
                  </div>
                </div>
                <div
                  className="flex items-center justify-between cursor-pointer p-2"
                  onClick={() => {
                    setCurrentPeriod("1년");
                    setTotalProfit(returnList["1년"]);
                    setPeriodSelectModal(false);
                  }}
                >
                  <span className={`${TYPO.TYPO5_1_MD} text-gray80 truncate`}>
                    {router.locale === "ko" ? "1년" : periodConvertEng("1년")}
                  </span>
                  <div className={currentPeriod === "1년" ? "" : "hidden"}>
                    <Check fill={"blue40"} height={20} width={20} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* <div className="flex gap-1">
          <span className={`${TYPO.TYPO4_SB} text-gray80`}>
            {t("estimatedTotalProfit")}
          </span>
          <span
            className={`${TYPO.TYPO4_SB} ${
              totalProfit > 0
                ? "text-red40"
                : totalProfit === 0
                ? "text-gray60"
                : "text-blue40"
            } `}
          >
            {totalProfit > 0 ? "+" : ""}
            {(buyAmount * totalProfit * 0.01).toLocaleString("en-us", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
            {router.locale === "ko" ? "원" : ""}
          </span>
        </div> */}
      </div>
      <div className="px-6 py-5">
        <div className="bg-gray2 rounded-[16px] overflow-hidden">
          <div className="flex justify-between  items-center h-[80px] ">
            <div
              className="px-5 py-4 cursor-pointer"
              onClick={() => {
                window.dataLayer.push({
                  event: "cStratCal",
                });
                if (buyAmount - 단위 < min) return;
                setBuyAmount(buyAmount - 단위);
              }}
            >
              <RemoveIcon />
            </div>
            <div className="flex flex-col items-center justify-center">
              <span className={`${TYPO.TYPO7_MD} text-gray60`}>
                {t("initialInvestment")}
              </span>
              <span className={`${TYPO.TYPO5_1_SB} text-gray80`}>
                {buyAmount.toLocaleString("en-us")}
                {router.locale === "ko" ? "원" : "$"}
              </span>
            </div>
            <div
              className="px-5 py-4 cursor-pointer"
              onClick={() => {
                window.dataLayer.push({
                  event: "cStratCal",
                });
                if (buyAmount + 단위 > max) return;
                setBuyAmount(buyAmount + 단위);
              }}
            >
              <AddIcon />
            </div>
          </div>
          <div
            className={`${
              viewExpectedProfit ? "" : "hover:bg-gray5"
            } border-t border-gray15 py-[10px] overflow-hidden`}
            data-gtm-id="stCalProfit"
            onClick={() => {
              trackPageAction("expand_strat_calc");
              setViewExpectedProfit(true);
            }}
            style={{
              maxHeight: viewExpectedProfit ? "116px" : "44px",
              transition: "max-height 0.3s ease",
            }}
          >
            {viewExpectedProfit ? (
              <div className="flex flex-col ">
                <div className="px-6 py-3 flex items-center gap-3">
                  <p className={`grow ${TYPO.TYPO5_1_MD} text-gray50`}>
                    예상 수익
                  </p>
                  <p
                    className={`${TYPO.TYPO5_1_MD} ${
                      totalProfit > 0
                        ? "text-red40"
                        : totalProfit === 0
                        ? "text-gray60"
                        : "text-blue40"
                    } `}
                  >
                    {totalProfit > 0 ? "+" : ""}
                    {(buyAmount * totalProfit * 0.01).toLocaleString("en-us", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    {router.locale === "ko" ? "원" : "$"}
                  </p>
                </div>
                <div className="px-6 py-3 flex items-center gap-3">
                  <p className={`grow ${TYPO.TYPO5_1_MD} text-gray50`}>
                    {t("totalValue")}
                  </p>
                  <p className={`${TYPO.TYPO5_1_MD} text-gray70`}>
                    {(buyAmount * (1 + totalProfit * 0.01)).toLocaleString(
                      "en-us",
                      {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }
                    )}
                    {""}
                    {router.locale === "ko" ? "원 예상" : "$ estimated"}
                  </p>
                </div>
              </div>
            ) : (
              <div className="items-center flex justify-center gap-[10px] cursor-pointer ">
                <p className={`${TYPO.TYPO7_MD} text-gray60`}>예상 수익 보기</p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                >
                  <path
                    d="M8.54011 10.1833L13.4401 5.28333C13.6068 5.11667 13.8012 5.03611 14.0234 5.04167C14.2457 5.04722 14.4401 5.13333 14.6068 5.3C14.7734 5.46667 14.8568 5.66111 14.8568 5.88333C14.8568 6.10555 14.7734 6.3 14.6068 6.46667L9.49011 11.6C9.35678 11.7333 9.20678 11.8333 9.04011 11.9C8.87345 11.9667 8.70678 12 8.54011 12C8.37345 12 8.20678 11.9667 8.04011 11.9C7.87345 11.8333 7.72345 11.7333 7.59011 11.6L2.45678 6.46667C2.29011 6.3 2.20956 6.10278 2.21511 5.875C2.22067 5.64722 2.30678 5.45 2.47345 5.28333C2.64011 5.11667 2.83456 5.03333 3.05678 5.03333C3.279 5.03333 3.47345 5.11667 3.64011 5.28333L8.54011 10.1833Z"
                    fill="var(--gray40)"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* <div className="py-2 flex flex-col gap-2">
        <div className="px-6 py-2 flex items-center gap-3">
          <span className={`${TYPO.TYPO5_1_MD} text-gray50 grow`}>
            {t("totalValue")}
          </span>
          <span className={`${TYPO.TYPO5_1_RG} text-gray70`}>
            {(buyAmount * (1 + totalProfit * 0.01)).toLocaleString("en-us", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
            {""}
            {router.locale === "ko" ? "원 예상" : "$ estimated"}
          </span>
        </div>
        <div className="px-6 py-2 flex items-center gap-3">
          <span className={`${TYPO.TYPO5_1_MD} text-gray50 grow`}>
            {t("return")}
          </span>
          <span className={`${TYPO.TYPO5_1_RG} text-gray70`}>
            {totalProfit > 0 ? "+" : ""}
            {totalProfit.toFixed(1)}%
            {router.locale === "ko" ? " 예상" : " estimated"}
          </span>
        </div>
      </div> */}
    </div>
  );
}

const AddIcon = () => {
  return (
    <div className="p-1 bg-gray10 rounded-[8px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M8.75 10.8333H3.54167C3.24653 10.8333 2.99913 10.7335 2.79948 10.5339C2.59983 10.3342 2.5 10.0868 2.5 9.79167C2.5 9.49653 2.59983 9.24913 2.79948 9.04948C2.99913 8.84983 3.24653 8.75 3.54167 8.75H8.75V3.54167C8.75 3.24653 8.84983 2.99913 9.04948 2.79948C9.24913 2.59983 9.49653 2.5 9.79167 2.5C10.0868 2.5 10.3342 2.59983 10.5339 2.79948C10.7335 2.99913 10.8333 3.24653 10.8333 3.54167V8.75H16.0417C16.3368 8.75 16.5842 8.84983 16.7839 9.04948C16.9835 9.24913 17.0833 9.49653 17.0833 9.79167C17.0833 10.0868 16.9835 10.3342 16.7839 10.5339C16.5842 10.7335 16.3368 10.8333 16.0417 10.8333H10.8333V16.0417C10.8333 16.3368 10.7335 16.5842 10.5339 16.7839C10.3342 16.9835 10.0868 17.0833 9.79167 17.0833C9.49653 17.0833 9.24913 16.9835 9.04948 16.7839C8.84983 16.5842 8.75 16.3368 8.75 16.0417V10.8333Z"
          fill="var(--gray50)"
        />
      </svg>
    </div>
  );
};

const RemoveIcon = () => {
  return (
    <div className="p-1 bg-gray10 rounded-[8px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M4.86979 11.1048C4.56313 11.1048 4.30243 10.9975 4.08771 10.7827C3.87299 10.5682 3.76562 10.3075 3.76562 10.0007C3.76562 9.69385 3.87299 9.43315 4.08771 9.21857C4.30243 9.00385 4.56313 8.89648 4.86979 8.89648H15.129C15.4356 8.89648 15.6963 9.00385 15.911 9.21857C16.1258 9.43315 16.2331 9.69385 16.2331 10.0007C16.2331 10.3075 16.1258 10.5682 15.911 10.7827C15.6963 10.9975 15.4356 11.1048 15.129 11.1048H4.86979Z"
          fill="var(--gray50)"
        />
      </svg>
    </div>
  );
};

export default StrategyCalculator;
